import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiWithAuth } from '../../lib/api';
import { API_BASE as API } from '../../lib/config';
import {
  ChevronLeftIcon,
  ChartBarIcon,
  CurrencyYenIcon,
  CalendarIcon,
  ArrowTrendingUpIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

const Earnings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [earnings, setEarnings] = useState([]);
  const [summary, setSummary] = useState({});
  const [selectedPeriod, setSelectedPeriod] = useState('current_month');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEarnings();
    // listen for instant wallet credit
    const onCredit = () => fetchEarnings();
    if (typeof window !== 'undefined') {
      window.addEventListener('wallet:credit', onCredit);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('wallet:credit', onCredit);
      }
    };
  }, [selectedPeriod]);

  const fetchEarnings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const client = apiWithAuth(token);
      // Try multiple endpoints and response shapes
      const candidates = [
        '/wallet/transactions',
        '/user/wallet/transactions',
        '/users/wallet/transactions',
        '/wallet/history',
      ];
      let txList = [];
      for (const path of candidates) {
        try {
          const res = await client.get(path);
          const d = res?.data;
          const arr = Array.isArray(d) ? d : (Array.isArray(d?.results) ? d.results : (Array.isArray(d?.data) ? d.data : null));
          if (arr) { txList = arr; break; }
        } catch (_) { /* try next */ }
      }

      // Map transactions to earning entries (best-effort)
      const parsed = (Array.isArray(txList) ? txList : []).map((t, idx) => ({
        id: t.id || String(idx),
        date: t.created_at || t.date || new Date().toISOString(),
        employer_name: t.employer_name || t.counterparty_name || '事業者',
        job_title: t.job_title || t.memo || 'お仕事',
        hours_worked: t.hours || t.duration_hours || 0,
        hourly_rate: t.hourly_rate || 0,
        gross_amount: t.amount || t.gross_amount || 0,
        tax_amount: t.tax_amount || 0,
        net_amount: typeof t.net_amount === 'number' ? t.net_amount : (t.amount || 0) - (t.tax_amount || 0),
        status: t.status || (t.settled ? 'paid' : 'processing')
      }));

      // Period filter
      const now = new Date();
      const startEnd = () => {
        const y = now.getFullYear();
        const m = now.getMonth();
        if (selectedPeriod === 'current_month') {
          return { start: new Date(y, m, 1), end: new Date(y, m + 1, 0, 23, 59, 59) };
        } else if (selectedPeriod === 'last_month') {
          return { start: new Date(y, m - 1, 1), end: new Date(y, m, 0, 23, 59, 59) };
        } else if (selectedPeriod === 'last_3_months') {
          return { start: new Date(y, m - 2, 1), end: new Date(y, m + 1, 0, 23, 59, 59) };
        } else if (selectedPeriod === 'this_year') {
          return { start: new Date(y, 0, 1), end: new Date(y, 11, 31, 23, 59, 59) };
        }
        return { start: new Date(0), end: new Date(8640000000000000) };
      };
      const { start, end } = startEnd();
      const filtered = parsed.filter(e => {
        const d = new Date(e.date);
        return d >= start && d <= end;
      });

      // Summary
      const total_earnings = filtered.reduce((s, e) => s + (e.net_amount ?? 0), 0);
      const total_hours = filtered.reduce((s, e) => s + (e.hours_worked ?? 0), 0);
      const jobs_completed = filtered.length;
      const average_hourly_rate = total_hours > 0 ? Math.round(total_earnings / total_hours) : 0;

      // Growth: compare current month vs last month
      const thisMonth = parsed.filter(e => {
        const d = new Date(e.date);
        return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
      });
      const lastMonth = parsed.filter(e => {
        const d = new Date(e.date);
        const ly = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        return d.getFullYear() === ly.getFullYear() && d.getMonth() === ly.getMonth();
      });
      const current_month_earnings = thisMonth.reduce((s, e) => s + (e.net_amount ?? 0), 0);
      const last_month_earnings = lastMonth.reduce((s, e) => s + (e.net_amount ?? 0), 0);

      setEarnings(filtered);
      setSummary({
        total_earnings,
        total_hours,
        average_hourly_rate,
        jobs_completed,
        current_month_earnings,
        last_month_earnings,
        this_year_earnings: parsed.filter(e => new Date(e.date).getFullYear() === now.getFullYear())
          .reduce((s, e) => s + (e.net_amount ?? 0), 0)
      });
    } catch (error) {
      console.error('Failed to fetch earnings:', error);
      console.error('Failed to fetch earnings:', error);
      setEarnings([]);
      setSummary({
        total_earnings: 0,
        total_hours: 0,
        average_hourly_rate: 0,
        jobs_completed: 0,
        current_month_earnings: 0,
        last_month_earnings: 0,
        this_year_earnings: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">支払済</span>;
      case 'pending':
        return <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium">支払待ち</span>;
      case 'processing':
        return <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">処理中</span>;
      default:
        return <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-xs font-medium">不明</span>;
    }
  };

  const periods = [
    { value: 'current_month', label: '今月' },
    { value: 'last_month', label: '先月' },
    { value: 'last_3_months', label: '過去3ヶ月' },
    { value: 'this_year', label: '今年' }
  ];

  const monthlyGrowth = summary.last_month_earnings > 0
    ? ((summary.current_month_earnings - summary.last_month_earnings) / summary.last_month_earnings * 100).toFixed(1)
    : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">読み込み中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <button
              onClick={() => navigate('/mypage')}
              className="mr-4 p-2 text-gray-600 hover:text-gray-900"
            >
              <ChevronLeftIcon className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-bold text-gray-900">獲得した報酬</h1>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 期間選択 */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">期間を選択</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {periods.map((period) => (
              <button
                key={period.value}
                onClick={() => setSelectedPeriod(period.value)}
                className={`px-4 py-2 rounded-lg font-medium ${selectedPeriod === period.value
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
              >
                {period.label}
              </button>
            ))}
          </div>
        </div>

        {/* 統計サマリー */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="bg-green-100 p-3 rounded-lg">
                <CurrencyYenIcon className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">総報酬</p>
                <p className="text-2xl font-bold text-gray-900">{summary.total_earnings?.toLocaleString()}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="bg-blue-100 p-3 rounded-lg">
                <ClockIcon className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">総勤務時間</p>
                <p className="text-2xl font-bold text-gray-900">{summary.total_hours}時間</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="bg-purple-100 p-3 rounded-lg">
                <ChartBarIcon className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">平均時給</p>
                <p className="text-2xl font-bold text-gray-900">{summary.average_hourly_rate?.toLocaleString()}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="bg-orange-100 p-3 rounded-lg">
                <ArrowTrendingUpIcon className="w-6 h-6 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">月間成長率</p>
                <p className={`text-2xl font-bold ${monthlyGrowth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {monthlyGrowth >= 0 ? '+' : ''}{monthlyGrowth}%
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* 報酬履歴 */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">報酬履歴</h3>
          </div>

          <div className="divide-y divide-gray-200">
            {earnings.length === 0 ? (
              <div className="p-12 text-center">
                <ChartBarIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h4 className="text-lg font-medium text-gray-900 mb-2">報酬履歴がありません</h4>
                <p className="text-gray-600">お仕事を完了すると報酬履歴が表示されます。</p>
              </div>
            ) : (
              earnings.map((earning) => (
                <div key={earning.id} className="p-6">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <h4 className="text-lg font-semibold text-gray-900 mr-3">
                          {earning.job_title}
                        </h4>
                        {getStatusBadge(earning.status)}
                      </div>

                      <p className="text-gray-600 mb-3">{earning.employer_name}</p>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div className="flex items-center text-gray-600">
                          <CalendarIcon className="w-4 h-4 mr-2" />
                          {formatDate(earning.date)}
                        </div>

                        <div className="flex items-center text-gray-600">
                          <ClockIcon className="w-4 h-4 mr-2" />
                          {earning.hours_worked}時間
                        </div>

                        <div className="text-gray-600">
                          時給: {earning.hourly_rate.toLocaleString()}
                        </div>

                        <div className="font-medium text-gray-900">
                          総額: {earning.gross_amount.toLocaleString()}
                        </div>
                      </div>

                      {earning.tax_amount > 0 && (
                        <div className="mt-3 bg-gray-50 p-3 rounded text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">支給額:</span>
                            <span>{earning.gross_amount.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">源泉徴収税額:</span>
                            <span>-{earning.tax_amount.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between border-t border-gray-200 pt-2 mt-2 font-medium">
                            <span>手取り額:</span>
                            <span>{earning.net_amount.toLocaleString()}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* 税務情報 */}
        <div className="mt-8 bg-blue-50 rounded-lg p-6">
          <div className="flex items-start">
            <ChartBarIcon className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-900 mb-2">税務に関するお知らせ</h4>
              <ul className="text-blue-800 text-sm space-y-1">
                <li> 年間所得が20万円を超える場合は確定申告が必要です</li>
                <li> 源泉徴収票は源泉徴収票確認ページからダウンロードできます</li>
                <li> 詳細な税務相談は税理士にご相談ください</li>
                <li> 交通費等の経費は適切に計上することができます</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Earnings;